import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { KidAuthProvider, useKidAuth } from "@/context/KidAuthContext";
import Landing from "@/pages/Landing";
import Auth from "@/pages/Auth";
import Browse from "@/pages/Browse";
import ListingDetail from "@/pages/ListingDetail";
import ParentDashboard from "@/pages/ParentDashboard";
import ParentHelpCenter from "@/pages/ParentHelpCenter";
import KidsManage from "@/pages/KidsManage";
import KidProfile from "@/pages/KidProfile";
import CreateListing from "@/pages/CreateListing";
import CheckoutSuccess from "@/pages/CheckoutSuccess";
import Orders from "@/pages/Orders";
import OrderDetail from "@/pages/OrderDetail";
import ShippingLabel from "@/pages/ShippingLabel";
import Contact from "@/pages/Contact";
import Pin from "@/pages/Pin";
import Info from "@/pages/Info";
import InfoHub from "@/pages/InfoHub";
import AppPortal from "@/pages/AppPortal";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import PublicShop from "@/pages/PublicShop";
import ParentLedger from "@/pages/ParentLedger";
import WalletStatement from "@/pages/WalletStatement";
import KidZone from "@/pages/KidZone";
import KidWorkspace from "@/pages/KidWorkspace";
import KidLedger from "@/pages/KidLedger";
import VerifyEmail from "@/pages/VerifyEmail";
import ParentReviews from "@/pages/ParentReviews";
import ParentModeration from "@/pages/ParentModeration";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminLedger from "@/pages/AdminLedger";
import AdminCsrPins from "@/pages/AdminCsrPins";
import CsrHub from "@/pages/CsrHub";
import PartnerAgreementPage from "@/pages/PartnerAgreementPage";
import VideoHub from "@/pages/VideoHub";
import RateOrder from "@/pages/RateOrder";
import { Toaster } from "@/components/ui/sonner";
import CookieConsent from "@/components/CookieConsent";

function ProtectedRoute({ children }) {
  const { user } = useAuth();
  if (user === null) return <div className="p-20 text-center text-slate-500">Loading…</div>;
  if (user === false) return <Navigate to="/auth?mode=login" replace />;
  return children;
}

/**
 * Iteration 176 — Shared parent/kid route gate. Accepts EITHER an
 * authenticated parent session OR an active kid session. Used by
 * `/orders` and `/orders/:id` so a signed-in kid tapping the Orders
 * stat card from `/kidzone/workspace` stays on the master orders view
 * instead of being bounced to the parent Sign-In page. Both sessions
 * are checked in parallel; we only redirect once we're certain neither
 * is present.
 */
function SharedProtectedRoute({ children }) {
  const { user } = useAuth();
  const { kid } = useKidAuth();
  // Still hydrating either context — hold the render so we don't
  // flash a redirect before the kid cookie has been read.
  if (user === null || kid === null) {
    return <div className="p-20 text-center text-slate-500">Loading…</div>;
  }
  const hasParent = !!user;
  const hasKid = !!(kid && kid.kid_id);
  if (!hasParent && !hasKid) {
    return <Navigate to="/auth?mode=login" replace />;
  }
  return children;
}

/**
 * Kid-scoped route gate. Redirects to /kidzone (the kid login page) when
 * there is no kid session — mirrors ProtectedRoute for parent auth.
 */
function KidProtectedRoute({ children }) {
  const { kid } = useKidAuth();
  if (kid === null) return <div className="p-20 text-center text-slate-500">Loading…</div>;
  if (kid === false || !kid.kid_id) return <Navigate to="/kidzone" replace />;
  return children;
}

/**
 * Iteration 186 — legacy email link redirect. Older seller emails
 * pointed at `/fulfilment/:id` and `/fulfillment/:id`; the canonical
 * fulfilment page is now `/orders/:id` (which houses the 4-digit PIN
 * entry form under the shared parent+kid gate). We forward the ID
 * through so the parent's magic-link click lands in the right place.
 */
function FulfilmentRedirect() {
  const { id } = useParams();
  return <Navigate to={`/orders/${id}`} replace />;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <KidAuthProvider>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/parent-auth" element={<Auth />} />
            <Route path="/auth/forgot" element={<ForgotPassword />} />
            <Route path="/auth/reset" element={<ResetPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify" element={<VerifyEmail />} />
            <Route path="/dashboard/reviews" element={<ProtectedRoute><ParentReviews /></ProtectedRoute>} />
            <Route path="/parent/moderation" element={<ProtectedRoute><ParentModeration /></ProtectedRoute>} />
            <Route path="/browse" element={<Browse />} />
            <Route path="/csr" element={<CsrHub />} />
            <Route path="/videos" element={<VideoHub />} />
            <Route path="/how-it-works-videos" element={<Navigate to="/videos" replace />} />
            <Route path="/legal/partner-agreement" element={<PartnerAgreementPage />} />
            <Route path="/rate-order" element={<RateOrder />} />
            <Route path="/shop/:kidId" element={<PublicShop />} />
            <Route path="/listing/:id" element={<ListingDetail />} />
            <Route path="/dashboard/parent" element={<ProtectedRoute><ParentDashboard /></ProtectedRoute>} />
            <Route path="/parent-help" element={<ParentHelpCenter />} />
            {/* Iteration 186 — legacy email links from older builds point at
                `/parent-dashboard` and `/fulfilment/:id`. Redirect them to
                the current canonical routes so parents never hit a 404. */}
            <Route path="/parent-dashboard" element={<Navigate to="/dashboard/parent" replace />} />
            <Route path="/fulfilment/:id" element={<FulfilmentRedirect />} />
            <Route path="/fulfillment/:id" element={<FulfilmentRedirect />} />
            <Route path="/dashboard/ledger" element={<ProtectedRoute><ParentLedger /></ProtectedRoute>} />
            {/* Iteration 306 — canonical deep-link target for the Kid Zone
                "Total earnings from sales" click-through. */}
            <Route path="/parent/ledger" element={<ProtectedRoute><ParentLedger /></ProtectedRoute>} />
            <Route path="/dashboard/wallet-statement" element={<ProtectedRoute><WalletStatement /></ProtectedRoute>} />
            <Route path="/kids" element={<ProtectedRoute><KidsManage /></ProtectedRoute>} />
            <Route path="/kids/:id" element={<ProtectedRoute><KidProfile /></ProtectedRoute>} />
            <Route path="/sell" element={<ProtectedRoute><CreateListing /></ProtectedRoute>} />
            <Route path="/orders" element={<SharedProtectedRoute><Orders /></SharedProtectedRoute>} />
            <Route path="/orders/:id" element={<SharedProtectedRoute><OrderDetail /></SharedProtectedRoute>} />
            <Route path="/orders/:id/label" element={<SharedProtectedRoute><ShippingLabel /></SharedProtectedRoute>} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/pin" element={<ProtectedRoute><Pin /></ProtectedRoute>} />
            <Route path="/checkout/success" element={<CheckoutSuccess />} />
            {/* Iteration 194 — canonical "Thank You" URL requested for
                marketing copy alignment. Both routes render the same
                confirmation experience; upstream success_url still
                lands on /checkout/success. */}
            <Route path="/order-confirmation" element={<CheckoutSuccess />} />
            {/* Alias — Stripe's success_url redirects here; query params
                (session_id etc.) are preserved automatically by the router. */}
            <Route path="/checkout-success" element={<CheckoutSuccess />} />
            <Route path="/info" element={<InfoHub />} />
            <Route path="/info/:topic" element={<Info />} />
            <Route path="/kidzone" element={<KidZone />} />
            <Route path="/kidzone/workspace" element={<KidProtectedRoute><KidWorkspace /></KidProtectedRoute>} />
            <Route path="/kidzone/ledger" element={<KidProtectedRoute><KidLedger /></KidProtectedRoute>} />
            <Route path="/app" element={<AppPortal />} />
            <Route path="/select" element={<Navigate to="/app" replace />} />
            {/* Iteration 99 — internal admin console (no parent-auth gate; the
                page itself gates on ADMIN_API_KEY). Also exposed at /owner
                for convenience. */}
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/admin/csr-pins" element={<AdminCsrPins />} />
            <Route path="/admin/ledger" element={<AdminLedger />} />
            <Route path="/owner" element={<Navigate to="/admin" replace />} />
            {/* Legacy redirects */}
            <Route path="/dashboard/kid" element={<Navigate to="/dashboard/parent" replace />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster position="top-center" richColors />
          <CookieConsent />
          </KidAuthProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
